export type ViewBackedAppFetch = (
  url: string,
  init?: RequestInit
) => Promise<Response>;

export class ViewBackedAppHttpError extends Error {
  readonly skipGlobalSWRToast = true;

  constructor(
    readonly status: number,
    readonly statusText = '',
    message = `View-backed app API request failed with HTTP ${status}`,
    name = 'ViewBackedAppHttpError'
  ) {
    super(message);
    this.name = name;
  }
}

export class ViewBackedAppServerUnavailableError extends Error {
  readonly skipGlobalSWRToast = true;

  constructor(
    message = 'Workspace server is not available',
    name = 'ViewBackedAppServerUnavailableError'
  ) {
    super(message);
    this.name = name;
  }
}

export async function fetchViewBackedAppJson<T>(
  url: string,
  init: RequestInit | undefined,
  fetcher: ViewBackedAppFetch,
  createHttpError: (
    status: number,
    statusText: string,
    message?: string
  ) => Error = (status, statusText, message) =>
    new ViewBackedAppHttpError(status, statusText, message)
): Promise<T> {
  const response = await fetcher(url, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...init?.headers,
    },
  });
  if (!response.ok) {
    throw createHttpError(
      response.status,
      response.statusText,
      await readErrorMessage(response)
    );
  }
  return (await response.json()) as T;
}

async function readErrorMessage(
  response: Response
): Promise<string | undefined> {
  const contentType = response.headers.get('content-type') ?? '';
  try {
    if (contentType.includes('application/json')) {
      const body = (await response.json()) as unknown;
      return errorMessageFromJson(body);
    }
    const text = await response.text();
    return text.trim() || undefined;
  } catch {
    return undefined;
  }
}

function errorMessageFromJson(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const message = (body as { message?: unknown }).message;
  if (typeof message === 'string') return message;
  if (Array.isArray(message)) {
    return message.filter(item => typeof item === 'string').join(', ');
  }
  const error = (body as { error?: unknown }).error;
  return typeof error === 'string' ? error : undefined;
}
